import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getGroups } from "../../api/group";
import Group from "../../models/group";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import GroupDeleteModal from "./GroupDeleteModal";
import GroupUpdateModal from "./GroupUpdateModal";

const ITEMS_PER_PAGE = 10;

const GroupList = () => {
  const fields = [
    { key: "name", _classes: ["text-center"], label: "Nombre" },
    {
      key: "description",
      _classes: ["text-center"],
      label: "Descripcion",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [showGroupEditModal, setShowGroupEditModal] = useState(false);
  const [showGroupDeleteModal, setShowGroupDeleteModal] = useState(false);
  const [readonlyModal, setReadonlyModal] = useState(false);
  const [group, setGroup] = useState<Group | undefined>(undefined);
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [, setFirstLoad] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";

    return { search: search, page: page };
  };

  const fetchGroups = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const searchValue = urlParams.search ? urlParams.search : undefined;
    const groupsStatus = await getGroups(limit, offset, searchValue);

    if (groupsStatus.status === SUCCESS) {
      if (groupsStatus.data !== undefined) {
        const count = groupsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);

        setTotalPages(pages);
        setGroups(groupsStatus.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = groupsStatus.detail
        ? groupsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set("search", previousParams.search ? previousParams.search : "");
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setReadonlyModal(false);
    setShowGroupEditModal(true);
  };

  const onDetailClick = (group: Group) => {
    setGroup(group);
    setReadonlyModal(true);
    setShowGroupEditModal(true);
  };

  const onUpdateClick = (group: Group) => {
    setGroup(group);
    setReadonlyModal(false);
    setShowGroupEditModal(true);
  };

  const onDeleteClick = (group: Group) => {
    setGroup(group);
    setShowGroupDeleteModal(true);
  };

  const onGroupEditCancel = () => {
    setGroup(undefined);
    setShowGroupEditModal(false);
  };

  const onGroupEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowGroupEditModal(false);
    setGroups([]);
    setLoading(true);
    fetchGroups();
  };

  const onGroupDeleteCancel = () => {
    setGroup(undefined);
    setShowGroupDeleteModal(false);
  };

  const onGroupDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowGroupDeleteModal(false);
    setGroups([]);
    setLoading(true);
    fetchGroups();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchGroups();
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("groups")) {
        return;
      }
      fetchGroups();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Todos los grupos</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Agregar Nuevo
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={groups}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    actions: (item: Group) => {
                      const detailButton = (
                        <CButton
                          className="text-white"
                          color="primary"
                          onClick={() => {
                            onDetailClick(item);
                          }}
                        >
                          <i className="fa fa-eye"></i>
                        </CButton>
                      );
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            {detailButton}
                            {editButton}
                            {deleteButton}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <GroupUpdateModal
        disabled={readonlyModal}
        show={showGroupEditModal}
        onCancel={onGroupEditCancel}
        onSuccess={onGroupEditSuccess}
        group={group}
      />
      <GroupDeleteModal
        show={showGroupDeleteModal}
        onCancel={onGroupDeleteCancel}
        onSuccess={onGroupDeleteSuccess}
        group={group}
      />
    </>
  );
};

export default GroupList;
